<template>
  <v-container fluid>
    <v-data-table
      :loading="loading"
      :headers="filteredHeaders"
      :items="filteredItemsList"
      :item-class="rowClass"
      :search="search"
      :hide-default-footer="hideFooter"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: 'Rodyti po:',
        itemsPerPageAllText: 'visi',
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :items-per-page="pagination.pageSize"
      :page.sync="pagination.page"
    >
      <template #top>
        <v-toolbar flat>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label=""
            single-line
            hide-details
          ></v-text-field>
          <v-spacer></v-spacer>
        </v-toolbar>
      </template>
      <template #[`item.registras`]="{ item }">
        <div class="nowrap">{{ item.registras }}</div>
      </template>
      <template #[`item.tipas_full`]="{ item }">
        <v-row dense no-gutters>
          <v-col cols="12" md="12">
            {{ item.tipas }}
          </v-col>
          <v-col cols="12" md="12">
            {{ item.unikalus_nr }}
          </v-col>
          <v-col cols="12" md="12">
            <span class="text-gray">{{ item.adresas }}</span>
          </v-col>
        </v-row>
      </template>
      <template #[`item.kaina`]="{ item }">
        {{ item.kainaFormat }}
      </template>
      <template #[`item.actions`]="{ item }">
        <el-table-action :item="item" @deleteItem="deleteItem(item)" />
      </template>
      <template #[`body.append`] v-if="!loading && turtaiYra">
        <tr>
          <td :colspan="3" class="text-end">
            <strong
              >{{ $t('Iš viso, EUR') }}:<el-tooltip
                inline
                :tooltip="`${pazymaTooltipPrefix} Kaina, Eur`"
            /></strong>
          </td>
          <td class="text-center ">
            <strong>{{ pazymaKainaFormat }}</strong>
          </td>
          <td></td>
        </tr>
      </template>
      <template slot="loading">
        Kraunasi...
      </template>
      <template slot="no-results">
        Duomenų nėra
      </template>
      <template slot="no-data">
        <div class="text-black">
          <el-savitarna-alert dense outlined type="warning2">
            {{ turtaiWarning }}
          </el-savitarna-alert>
        </div>
      </template>
      <template #[`footer.page-text`]="props">
        Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
        {{ props.itemsLength }}
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import PazymaMap from './PazymaMap'
import math from '@/mixins/math'
import rowClass from '@/mixins/rowClass'

export default {
  name: 'PazymaTurtas',
  mixins: [math, rowClass],
  props: {
    pazymaKodas: String,
    pazyma: Object,
  },
  data() {
    return {
      loading: true,
      search: null,
      searchData: {},
      turtai: [],

      itemsPerPageOptions: [10, 50, 100, -1],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
        sort: null,
        sortDesc: [],
        direction: 'asc',
      },
    }
  },
  computed: {
    ...mapGetters(['pazymaID']),
    headers() {
      return [
        {
          text: this.$t('Registro numeris'),
          align: 'start',
          sortable: false,
          value: 'registras_nr',
          width: 100,
        },
        {
          text: this.$t('Turto informacija'),
          align: 'start',
          sortable: false,
          value: 'tipas_full',
        },
        {
          text: this.$t('Savininkas'),
          align: 'start',
          sortable: false,
          value: 'savininkas',
        },
        {
          text: this.$t('Kaina, Eur'),
          align: 'center',
          sortable: false,
          value: 'kainaFormat',
          width: 80,
        },
        {
          text: this.$t('Veiksmai'),
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 80,
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    turtaiViso() {
      return this.turtai.length
    },
    turtaiYra() {
      return this.turtaiViso > 0
    },
    filteredItemsList() {
      return this.turtai.map(turtas => {
        turtas.canDelete = !this.isReadonly && this.turtaiViso > 1

        turtas.kainaFormat =
          turtas.uzsakomas > 0
            ? this.moneyformat(this.turtoKaina)
            : this.neuzsakomas

        turtas.tipas_full = turtas.tipas + turtas.unikalus_nr + turtas.adresas
        return turtas
      })
    },
    hideFooter() {
      return this.pagination.total <= 10
    },
    isReadonly() {
      return this.pazyma?.paz_readonly
    },
    pazymaMap() {
      return PazymaMap.getPazymaTipas(this.pazymaKodas)
    },
    pazymaTipas() {
      return this.pazymaMap.tipas
    },
    turtaiTitle() {
      return this.pazymaMap.turtaiTitle
    },
    turtaiWarning() {
      if (this.pazyma?.paz_su_igaliojimu) {
        return this.pazymaMap.turtaiIgaliojimasWarning
      }
      return this.pazymaMap.turtaiWarning
    },
    neuzsakomas() {
      return this.$t('Užsakymas galimas tik RC padalinyje')
    },
    turtoKaina() {
      if (!this.pazyma) {
        return 0
      }

      let visoUzsakomi = 0
      this.turtai.forEach(turtas => {
        if (turtas.uzsakomas) {
          visoUzsakomi++
        }
      })

      if (visoUzsakomi > 0) {
        return this.pazyma.paz_kaina / visoUzsakomi
      }

      return 0
    },
    pazymaKainaFormat() {
      return this.pazyma && this.pazyma.paz_kaina
        ? this.moneyformat(this.pazyma.paz_kaina)
        : this.moneyformat(0)
    },
    pazymaTooltipPrefix() {
      return 'NtrPaslaugos. Pažyma. Žemės sklypas.'
    },
  },
  created() {
    this.getData()
  },
  methods: {
    getData() {
      this.loading = true

      this.$axios
        .get(`ntrpaslaugos/pazyma/${this.pazymaID}/turtas`)
        .then(res => {
          this.turtai = res.data.turtas
          this.pagination.total = this.turtai.length
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.$emit('loaded', this.turtai.length)
        })
    },
    deleteItem(item) {
      const data = {
        turtas_id: item.turtas_id,
      }

      this.$axios
        .delete(`ntrpaslaugos/pazyma/${this.pazymaID}/turtas`, { data })
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.updateList()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {})
    },
    updateList() {
      this.getData()
      this.$emit('updatePazyma')
    },
    getActiveClass(item) {
      if (!item.uzsakomas) {
        return 'row-red'
      }
      return ''
    },
  },
}
</script>
<style lang="scss" scoped>
.text-black {
  color: rgba(0, 0, 0, 0.87);
}
</style>
